import { OrderLineItemData } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import { Image, LineItemNote, OrderMobileLineItem, SmartLink } from '@components';
import { Box, Stack, styled } from '@mui/material';
import { breakpoints } from '@themes/theme.helpers';
import { t } from '@translations';
import { getImageResizerUrl } from '@utils/images.utils';
import { formatPrice } from '@utils/price.utils';
import { insertWordBreaks } from '@utils/text.utils';

type OrderLineItemProps = {
  lineItem?: OrderLineItemData;
};
// STYTLES
//----------------

const ImageWrapper = styled(Box)`
  img {
    height: 100px;
    width: 100px;
  }
`;

const NameWrapper = styled(Box)`
  font-weight: 700;
  text-transform: uppercase;
  word-break: break-word;
`;

const MobileColumnLabel = styled(Box)`
  font-weight: 700;
  width: 150px;
  ${breakpoints.up('lg')} {
    display: none;
  }
`;

export const OrderBox = styled(Box)`
  background-color: #fff;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

export const OrderItemWrapper = styled(Box)`
  padding: 16px;
  background: #fff;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  width: 100%;
  ${breakpoints.up('lg')} {
    justify-content: space-between;
    flex-direction: row;
    padding: 16px 0 8px;
  }
`;
export const OrderCell = styled(Box)`
  display: flex;

  ${breakpoints.up('lg')} {
    align-items: center;
    width: 150px;
    justify-content: center;

    &.bold {
      font-weight: 700;
    }
    &.uppercase {
      text-transform: uppercase;
    }
    &.long {
      flex-grow: 1;
      justify-content: flex-start;
    }

    &.actions {
      width: 80px;
      text-align: right;
    }

    &.actions-with-favorites {
      width: 100px;
      text-align: right;
    }

    &.center {
      justify-content: center;
      text-align: right;
    }
  }
`;
/**
 * Single Order item
 */
export const OrderLineItem = (props: OrderLineItemProps) => {
  const { lineItem = {} } = props;
  const {
    name,
    productNo = '',
    price,
    total,
    discount,
    netPrice,
    imageUrl = '',
    quantity = 0,
    url = '',
  } = lineItem;

  const resizedImageUrl = getImageResizerUrl(imageUrl, { width: 100, format: 'webp' });

  return (
    <Box>
      <OrderMobileLineItem {...props} quantity={quantity} netPrice={price} totalNetPrice={total} />
      <Box sx={{ display: { xs: 'none', lg: 'block' }, height: '100%' }}>
        <OrderBox sx={{ height: '100%' }}>
          <OrderItemWrapper>
            <OrderCell sx={{ justifyContent: 'left !important' }}>
              <ImageWrapper sx={{ ml: 2 }}>
                <Image image={resizedImageUrl} />
              </ImageWrapper>
            </OrderCell>
            <OrderCell className='long' sx={{ height: '100%' }}>
              <Stack direction='column' gap={1} sx={{ height: '100%', justifyContent: 'center' }}>
                <SmartLink href={url}>
                  <NameWrapper
                    dangerouslySetInnerHTML={{ __html: insertWordBreaks(name) }}
                  ></NameWrapper>
                </SmartLink>
                <Box sx={{ width: 'min-content' }}>
                  {t(TextCodes.Product.Details.ProductNumber, { productNo })}
                </Box>
              </Stack>
            </OrderCell>
            <OrderCell>{formatPrice(price)}</OrderCell>
            <OrderCell>{formatPrice(discount)}</OrderCell>
            <OrderCell>{formatPrice(netPrice)}</OrderCell>
            <OrderCell className='bold center'>
              <MobileColumnLabel>{t(TextCodes.Order.Details.QuantityColumn)}</MobileColumnLabel>
              {quantity}
            </OrderCell>

            <OrderCell className='bold'>
              <MobileColumnLabel>{t(TextCodes.Order.Details.TotalColumn)}</MobileColumnLabel>
              {formatPrice(total)}
            </OrderCell>
          </OrderItemWrapper>
          <Stack direction='row'>
            <Box sx={{ width: '150px' }}></Box>
            <LineItemNote lineItem={lineItem} p={'0 0 16px 0'} />
          </Stack>
        </OrderBox>
      </Box>
    </Box>
  );
};
